import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useForm } from '@sequencehq/utils'
import { SCHEDULE_COMPLETED_STATUSES } from 'BillingScheduleHome/billingScheduleHome.constants'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { dequal } from 'dequal'
import { StripeInspectorItem, StripePaymentWidget } from 'Integrations/index'
import { StripeAutoChargeWidget } from 'Integrations/integrationsConfig/stripe/InspectorItem/widgets/StripeAutoChargeWidget'
import { useNotifications } from '@sequencehq/core-components'
import { useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { usePermissions } from 'lib/rbac'

const useStripeInspectorItem = (props: { billingScheduleId: string }) => {
  const { displayNotification } = useNotifications()
  const { can } = usePermissions()
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId: props.billingScheduleId,
    select: data => {
      return {
        rawSettingsData: data.billingScheduleSettings,
        billingScheduleStatus: data.billingSchedule.status,
        acceptPaymentsViaStripe:
          data?.billingScheduleSettings?.paymentProvider === 'STRIPE',
        autoChargeViaStripe: data?.billingScheduleSettings?.autoCharge,
        otherProviderActive:
          data?.billingScheduleSettings?.paymentProvider !== 'STRIPE' &&
          data?.billingScheduleSettings?.paymentProvider !== 'NONE'
      }
    }
  })

  const scheduleSettingsMutation = useMutation(
    dashboard20240730Client.putBillingScheduleSettings
  )

  /**
   * We don't want to DDoS the service if someone gets silly with the sliders,
   * so we debounce.
   */
  const syncDataToService = useDebouncedCallback(
    (newData: {
      acceptPaymentsViaStripe: boolean
      autoChargeViaStripe: boolean
    }) => {
      if (!billingScheduleAggregateQuery.data?.rawSettingsData?.id) {
        return
      }

      /**
       * Because this is a PUT (grumble grumble) we need to grab the _entire_
       * previous object and include it with the mutation, so we don't lose
       * data.
       */
      scheduleSettingsMutation.mutate({
        id: billingScheduleAggregateQuery.data?.rawSettingsData?.id,
        body: {
          ...billingScheduleAggregateQuery.data?.rawSettingsData,
          paymentProvider: newData.acceptPaymentsViaStripe ? 'STRIPE' : 'NONE',
          autoCharge: newData.autoChargeViaStripe
        }
      })
    },
    250
  )

  const initialFormValue = useMemo(
    () => ({
      acceptPaymentsViaStripe:
        billingScheduleAggregateQuery.data?.acceptPaymentsViaStripe ?? false,
      autoChargeViaStripe:
        billingScheduleAggregateQuery.data?.autoChargeViaStripe ?? false
    }),
    [billingScheduleAggregateQuery]
  )

  const form = useForm({
    value: initialFormValue,
    fieldConfiguration: [
      {
        property: 'acceptPaymentsViaStripe'
      },
      {
        property: 'autoChargeViaStripe',
        disabled: ctx => ctx.formData.acceptPaymentsViaStripe === false
      }
    ],
    onChange: newData => {
      syncDataToService({
        acceptPaymentsViaStripe: newData.acceptPaymentsViaStripe,
        autoChargeViaStripe: newData.autoChargeViaStripe
      })
    }
  })

  /**
   * If we encounter an error with the mutation, reset the data to the value
   * from the server, and notify the user.
   */
  useEffect(() => {
    if (
      scheduleSettingsMutation.error &&
      !dequal(initialFormValue, form.queries.formData)
    ) {
      form.mutators.resetFormData()
      scheduleSettingsMutation.reset()
      displayNotification('Could not save changes to Stripe settings', {
        type: 'error'
      })
    }
  }, [
    scheduleSettingsMutation.reset,
    form.mutators.resetFormData,
    scheduleSettingsMutation.error,
    initialFormValue,
    form.queries.formData,
    displayNotification
  ])

  const canManageBilling = can('org.billing:update')

  const otherProviderActive =
    billingScheduleAggregateQuery.data?.otherProviderActive ?? false

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    isAvailable:
      canManageBilling &&
      billingScheduleAggregateQuery.data?.billingScheduleStatus &&
      !SCHEDULE_COMPLETED_STATUSES.includes(
        billingScheduleAggregateQuery.data?.billingScheduleStatus
      ),
    isMutations: scheduleSettingsMutation.isPending,
    otherProviderActive,
    formFields: form.fields
  }
}

export const BillingScheduleStripeSettingsInspectorItem = (props: {
  billingScheduleId: string
}) => {
  const { formFields, isLoading, isAvailable, otherProviderActive } =
    useStripeInspectorItem(props)
  const { can } = usePermissions()
  const canManageBilling = can('org.billing:update')

  return (
    <StripeInspectorItem
      alwaysShow
      enabled={formFields.acceptPaymentsViaStripe.value}
    >
      <StripePaymentWidget
        fieldData={{
          acceptPaymentsViaStripe: {
            value: formFields.acceptPaymentsViaStripe.value,
            disabled: isLoading
          }
        }}
        onChange={newData =>
          formFields.acceptPaymentsViaStripe.onChange(
            newData.acceptPaymentsViaStripe.value
          )
        }
        disabled={
          !isAvailable || isLoading || !canManageBilling || otherProviderActive
        }
        conflictingProviderActive={otherProviderActive}
        scope="billingSchedule"
      />
      {formFields.acceptPaymentsViaStripe.value ? (
        <StripeAutoChargeWidget
          fieldData={{
            autoChargeViaStripe: {
              value: formFields.autoChargeViaStripe.value,
              disabled: isLoading
            }
          }}
          onChange={newData =>
            formFields.autoChargeViaStripe.onChange(
              newData.autoChargeViaStripe.value
            )
          }
          disabled={!isAvailable || isLoading || !canManageBilling}
        />
      ) : null}
    </StripeInspectorItem>
  )
}
